import React, { createContext, useState, useEffect, useContext } from 'react';
import axios from 'axios';

const AuthContext = createContext(null);

// Set up axios interceptor ONCE at module level to avoid duplicate interceptors
let interceptorSetup = false;

const setupAxiosInterceptor = () => {
  if (interceptorSetup) return;
  interceptorSetup = true;
  
  axios.interceptors.request.use(
    (config) => {
      // Always read token from localStorage at request time
      // This ensures the latest token is used even during race conditions
      const token = localStorage.getItem('auth_token');
      if (token) {
        config.headers.Authorization = `Bearer ${token}`;
      }
      return config;
    },
    (error) => {
      return Promise.reject(error);
    }
  );
  
  console.log('✅ Axios interceptor configured for authentication');
};

// Set up interceptor immediately when module loads
setupAxiosInterceptor();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children, apiUrl }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [loading, setLoading] = useState(true);

  // Load token from localStorage on mount
  useEffect(() => {
    const storedToken = localStorage.getItem('auth_token');
    const storedUser = localStorage.getItem('auth_user');
    
    if (storedToken && storedUser) {
      setToken(storedToken);
      try {
        setUser(JSON.parse(storedUser));
      } catch (e) {
        console.error('Error parsing stored user:', e);
        localStorage.removeItem('auth_token');
        localStorage.removeItem('auth_user');
      }
    }
    setLoading(false);
  }, []);

  const login = async (username, password) => {
    try {
      const response = await axios.post(`${apiUrl}/auth/login`, {
        username,
        password
      });
      
      const { access_token, user_id, username: responseUsername } = response.data;
      
      setToken(access_token);
      localStorage.setItem('auth_token', access_token);
      
      const meResponse = await axios.get(`${apiUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      const userData = {
        id: user_id,
        username: responseUsername,
        email: meResponse.data.email,
        user_role: meResponse.data.user_role || 'family',
        linked_profile_id: meResponse.data.linked_profile_id,
        is_admin: meResponse.data.is_admin || false,
      };
      
      setUser(userData);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Login failed. Please check your credentials.'
      };
    }
  };

  const register = async (username, email, password) => {
    try {
      const response = await axios.post(`${apiUrl}/auth/register`, {
        username,
        email,
        password
      });
      
      const { access_token, user_id, username: responseUsername, pending_approval, message } = response.data;

      if (pending_approval || !access_token) {
        return {
          success: false,
          pending_approval: true,
          message: message || 'Registration received. An administrator will activate your account before you can sign in.'
        };
      }
      
      setToken(access_token);
      localStorage.setItem('auth_token', access_token);

      const meResponse = await axios.get(`${apiUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      const userData = {
        id: user_id,
        username: responseUsername,
        email: meResponse.data.email,
        user_role: meResponse.data.user_role || 'family',
        linked_profile_id: meResponse.data.linked_profile_id,
        is_admin: meResponse.data.is_admin || false,
      };

      setUser(userData);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Register error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Registration failed. Please try again.'
      };
    }
  };

  const loginWithGoogle = async (googleToken) => {
    try {
      const response = await axios.post(`${apiUrl}/auth/google/verify`, {
        token: googleToken
      });
      
      const { access_token, user_id, username: responseUsername } = response.data;
      
      setToken(access_token);
      localStorage.setItem('auth_token', access_token);

      const meResponse = await axios.get(`${apiUrl}/auth/me`, {
        headers: { Authorization: `Bearer ${access_token}` }
      });
      const userData = {
        id: user_id,
        username: responseUsername,
        email: meResponse.data.email,
        user_role: meResponse.data.user_role || 'family',
        linked_profile_id: meResponse.data.linked_profile_id,
        is_admin: meResponse.data.is_admin || false,
      };

      setUser(userData);
      localStorage.setItem('auth_user', JSON.stringify(userData));
      
      return { success: true, user: userData };
    } catch (error) {
      console.error('Google login error:', error);
      return {
        success: false,
        error: error.response?.data?.detail || 'Google login failed'
      };
    }
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    localStorage.removeItem('auth_token');
    localStorage.removeItem('auth_user');
    delete axios.defaults.headers.common['Authorization'];
  };

  const value = {
    user,
    token,
    login,
    register,
    logout,
    loginWithGoogle,
    isAuthenticated: !!token,
    loading
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
